import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { useConfirm } from '../../components/confirm';
import { useNotify } from '../../components/notifications';
import { AppToolbar, type ExportData } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { DateRangeSelect } from '../modules/DateRangeSelect';
import { fmtLocal as fmtLocalUtc, fmtLocalDateTime } from '../../utils/datetime';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
/**
 * One row of `AI_UserActivityLog`.
 *
 * Timestamps are the SERVER's local wall-clock, not UTC — the column default is
 * `SYSDATETIME()` under ADR-0008's temporary kill-switch, and the shared
 * `fmtLocal*` helpers render the literal stored value while that is on. The
 * live-session view is projected to the same zone server-side, so both views
 * agree on screen.
 */
interface UserActivityRow {
  id:         number;
  eventTime:  string;
  userName:   string;
  eventType:  string;       // see ACTIVITY_META
  ipAddress:  string | null;
  userAgent:  string | null;
  detail:     string | null;
  durationMs: number | null;   // populated on session-ending events
  sessionId:  string | null;   // pairs a Login with the row that ended it
  location:   string | null;   // "City, Country" resolved server-side (geo)
}

/** One live session from `AI_UserSession` — who is signed in RIGHT NOW. */
interface ActiveSessionRow {
  sessionId:      string;
  userName:       string;
  /** A licence is held per USER, not per session — the Release action targets this. */
  userOid:        string;
  signedInAt:     string;
  lastSeenAt:     string;
  sessionSeconds: number;
  idleSeconds:    number;
  ipAddress:      string | null;
  userAgent:      string | null;
  isExempt:       boolean;     // support account — signed in, but unlicensed
  /** Server-computed: may this user's licence be released?  True only when NONE
   *  of their sessions is still heartbeating — i.e. the session was left behind
   *  by a power cut or a browser that closed without signing out.  Mirrored here
   *  for the UI; the API enforces it independently (§14a Rule C). */
  canRelease:     boolean;
  /** Is THIS session still beating?  Lets the row say which machine is alive. */
  isResponding:   boolean;
  location:       string | null;
}

interface ActiveMeta {
  tracking:           boolean;   // false while enforcement is off (no rows are written)
  activeUsers:        number;    // distinct LICENSED users
  activeSessions:     number;    // total sessions (one user may hold several)
  idleTimeoutMinutes: number;
  /** Seconds without a heartbeat before a session counts as dead. */
  staleAfterSeconds?: number;
  message?:           string;
}

/** What an access log is asked for.  'history' is one row per EVENT;
 *  'sessions' is one row per SIGN-IN/SIGN-OUT PAIR — the plain "when did they
 *  come and go?" question, which in the event view means matching a Login row
 *  to a Logout row hours further down the list.  'insights' answers the two
 *  questions that only exist across many sessions: is the licence big enough,
 *  and is anyone failing to sign in repeatedly. */
type LogView = 'history' | 'active' | 'sessions' | 'insights';

/** Tab → the EventType(s) sent to the server. A tab may cover more than one
 *  type (a sign-out is either a deliberate Logout or a SessionTimeout), which
 *  the API accepts as a CSV so it stays one round trip. */
const EVENT_FILTERS = {
  all:      null,
  signin:   'Login,LoginFailed',
  signout:  'Logout,SessionTimeout',
  nav:      'Navigation',
} as const;
type EventFilter = keyof typeof EVENT_FILTERS;
type DateRangeKey = '24h' | '7d' | '30d' | '90d' | 'all';

/** Server-side page size. The old page hard-coded 1000 with no total, so any
 *  busier range silently hid the rest AND reported wrong KPIs. */
const PAGE_STEP = 1000;
const MAX_ROWS  = 5000;   // the reader's own ceiling — asking for more is futile

// ─────────────────────────────────────────────────────────────────────────────
// Date helpers — server returns UTC, we render in the viewer's local TZ.
// ─────────────────────────────────────────────────────────────────────────────
const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

/** Parse a server timestamp AS UTC.  SQL DATETIME2 is serialized without a
 *  time-zone marker (e.g. "2026-05-28T00:52:41"); plain `new Date()` would treat
 *  that as LOCAL time and double-shift it.  We force UTC by appending 'Z' when
 *  the string carries no 'Z' and no ±hh:mm offset — so the conversion to the
 *  viewer's zone is always correct (fixes the "time looks wrong" report). */
function parseUtc(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  let s = iso.trim();
  if (!/[zZ]$/.test(s) && !/[+-]\d{2}:?\d{2}$/.test(s)) s += 'Z';
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

/** Local date + time in the viewer's zone, 24-hour format (no AM/PM, no UTC).
 *  Delegates to the shared `fmtLocal` helper (§14b.6 — never roll your own);
 *  `parseUtc` above is retained only for the sort/duration math below. */
function fmtLocal(iso: string | null | undefined): string {
  return fmtLocalUtc(iso) || '—';
}

function cutoffSinceUtc(range: DateRangeKey): string | undefined {
  if (range === 'all') return undefined;
  const now = new Date();
  const ms = { '24h': 86400e3, '7d': 7 * 86400e3, '30d': 30 * 86400e3, '90d': 90 * 86400e3 }[range];
  return new Date(now.getTime() - ms).toISOString();
}

/** Compact user-agent → "Chrome 132 on Windows" style label.  We don't ship a
 *  full UA-parser; the regex hits the 95% case which is good enough for the
 *  audit table.  Full string still shows on hover. */
function uaShort(ua: string | null): string {
  if (!ua) return '—';
  let browser = 'Browser';
  const m1 = /(Edg|Chrome|Firefox|Safari|OPR)\/(\d+)/.exec(ua);
  if (m1) browser = `${m1[1] === 'OPR' ? 'Opera' : m1[1] === 'Edg' ? 'Edge' : m1[1]} ${m1[2]}`;
  let os = '';
  if (/Windows NT 10\.0/.test(ua)) os = 'Windows';
  else if (/Mac OS X/.test(ua))    os = 'macOS';
  else if (/Android/.test(ua))     os = 'Android';
  else if (/iPhone|iPad/.test(ua)) os = 'iOS';
  else if (/Linux/.test(ua))       os = 'Linux';
  return os ? `${browser} on ${os}` : browser;
}

/** OS-only label for the device-type icon. */
function osIcon(ua: string | null): string {
  if (!ua) return 'ti-device-desktop';
  if (/Android/.test(ua) || /iPhone/.test(ua)) return 'ti-device-mobile';
  if (/iPad/.test(ua))                          return 'ti-device-tablet';
  return 'ti-device-desktop';
}

/** Derive a human location label from the client IP — fully offline / privacy
 *  safe (no external geo-IP lookups, which the security model forbids).  For an
 *  on-prem LIMS almost every hit is a private LAN address, so we classify into
 *  Localhost / Internal Network / External rather than guessing a city. */
function ipLocation(ip: string | null): { label: string; bg: string; fg: string; icon: string } {
  const ext  = { label: 'External',         bg: 'var(--ai-pill-alert-bg)', fg: 'var(--ai-pill-alert-fg)', icon: 'ti-world' };
  const intl = { label: 'Internal Network', bg: 'var(--ai-pill-info-bg)', fg: 'var(--ai-pill-info-fg)', icon: 'ti-building' };
  const loc  = { label: 'Localhost',        bg: 'var(--ai-pill-neutral-bg)', fg: 'var(--ai-pill-neutral-fg)', icon: 'ti-server-2' };
  const none = { label: '—',                bg: 'transparent', fg: 'var(--ai-muted)', icon: 'ti-help' };
  if (!ip) return none;
  const v = ip.trim().toLowerCase();
  if (v === '::1' || v === '127.0.0.1' || v.startsWith('::ffff:127.')) return loc;
  // RFC-1918 private + IPv6 link-local / unique-local ranges.
  if (/^10\./.test(v) ||
      /^192\.168\./.test(v) ||
      /^172\.(1[6-9]|2\d|3[01])\./.test(v) ||
      /^::ffff:(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(v) ||
      v.startsWith('fe80') || v.startsWith('fc') || v.startsWith('fd'))
    return intl;
  return ext;
}

/** Location label for a row: prefer the server-resolved "City, Country" (real
 *  geo); otherwise fall back to the offline Localhost / Internal / External
 *  classifier. Returns the text + pill colours + icon. */
function locationLabel(r: UserActivityRow): { label: string; bg: string; fg: string; icon: string } {
  if (r.location && r.location.trim())
    return { label: r.location.trim(), bg: 'var(--ai-pill-ok-bg)', fg: 'var(--ai-pill-ok-fg)', icon: 'ti-map-pin' };
  return ipLocation(r.ipAddress);
}

/** Action chip metadata — turns the raw EventType into a short, readable verb
 *  with an icon + pill colour.  "Navigation" events become "Opened" so the
 *  audit table reads like a story of what the user did. */
export function activityMeta(eventType: string): { verb: string; icon: string; cls: string } {
  switch (eventType) {
    case 'Login':          return { verb: 'Signed in',   icon: 'ti-login-2',       cls: 'ok'    };
    case 'LoginFailed':    return { verb: 'Sign-in failed', icon: 'ti-lock-x',     cls: 'alert' };
    case 'Logout':         return { verb: 'Signed out',  icon: 'ti-logout-2',      cls: 'warn' };
    // Distinct from "Signed out" ON PURPOSE: the user did not leave, the app
    // dropped them. Collapsing the two would hide exactly the case an admin is
    // usually investigating ("was that a real sign-out, or did they crash?").
    case 'SessionTimeout': return { verb: 'Timed out',   icon: 'ti-clock-x',       cls: 'warn' };
    case 'Navigation':     return { verb: 'Opened',      icon: 'ti-pointer',       cls: 'info' };
    // Export/Save/Approve pills used to be rendered here for event types NOTHING
    // ever wrote — dead branches that made the log look richer than it was.
    default:               return { verb: eventType || '—', icon: 'ti-activity',   cls: 'info' };
  }
}

/** Turn the machine-readable `reason=…` the server records into plain words.
 *  Keeps the raw token out of the operator's face while staying exact. */
const REASON_TEXT: Record<string, string> = {
  logout:         'Clicked Sign Out',
  tabclose:       'Closed the browser',
  idle:           'No activity — timed out',
  expired:        'Sign-in expired (24 h)',
  admin:          'Ended by an administrator',
  badcredentials: 'Wrong username or password',
  badcode:        'Wrong verification code',
  seatlimit:      'Refused — all licences in use',
};

/** Extract `reason=x` from Detail and render it readably; returns '' if absent. */
export function reasonText(detail: string | null): string {
  if (!detail) return '';
  const m = /(?:^|;)reason=([a-z]+)/i.exec(detail);
  if (!m) return '';
  return REASON_TEXT[m[1]!.toLowerCase()] ?? m[1]!;
}

/** "1h 24m" / "12m" / "48s" — a duration a human reads at a glance. */
export function fmtDuration(ms: number | null | undefined): string {
  if (ms == null || ms < 0) return '';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

/** Same, from whole seconds (the live view counts in seconds). */
export const fmtSeconds = (sec: number) => fmtDuration(sec * 1000);

/** What landed in the Navigation / Detail column.  For a session event the
 *  Detail holds `reason=…`, which is rendered as words instead. */
function navText(r: UserActivityRow): string {
  return reasonText(r.detail) || (r.detail ?? '');
}

// ─────────────────────────────────────────────────────────────────────────────
// Column definitions — same shape Sampling Allocation uses.
// ─────────────────────────────────────────────────────────────────────────────
interface ColDef {
  key:    keyof UserActivityRow | string;
  header: string;
  width?: string;
  render?: (v: unknown, r: UserActivityRow) => React.ReactNode;
}

const COLS: ColDef[] = [
  { key: 'eventTime',  header: 'Date / Time', width: '175px',
    render: (_v, r) => (
      <span style={{ fontWeight: 600, color: 'var(--ai-ink)', whiteSpace: 'nowrap' }}>
        {fmtLocal(r.eventTime)}
      </span>
    ),
  },
  { key: 'userName',   header: 'User',        width: '150px',
    render: v => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, color: 'var(--ai-ink)' }}>
        <i className="ti ti-user-circle" style={{ fontSize: 15, color: 'var(--ai-muted)' }} />
        {(v as string) || '—'}
      </span>
    ),
  },
  { key: 'eventType',  header: 'Action',      width: '120px',
    render: v => {
      const a = activityMeta((v as string) || '');
      return (
        <span className={`bl-pill ${a.cls}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
          <i className={`ti ${a.icon}`} style={{ fontSize: 12 }} /> {a.verb}
        </span>
      );
    },
  },
  // Why it happened. Previously omitted entirely, which meant the grid could not
  // distinguish a deliberate sign-out from an idle timeout — the single most
  // useful fact on a sign-out row.
  { key: 'detail',     header: 'Reason / Page', width: '210px',
    render: (_v, r) => {
      const t = navText(r);
      return t
        ? <span title={r.detail ?? ''} style={{ color: 'var(--ai-ink)' }}>{t}</span>
        : <span style={{ color: 'var(--ai-muted)' }}>—</span>;
    },
  },
  // Real session length, measured by the DATABASE clock on the ending event.
  // The column existed in the schema but was NULL in 100% of rows.
  { key: 'durationMs', header: 'Duration',    width: '95px',
    render: (_v, r) => {
      const t = fmtDuration(r.durationMs);
      return t
        ? <span style={{ color: 'var(--ai-ink)', whiteSpace: 'nowrap' }}>{t}</span>
        : <span style={{ color: 'var(--ai-muted)' }}>—</span>;
    },
  },
  { key: 'location',   header: 'Location',    width: '180px',
    render: (_v, r) => {
      const loc = locationLabel(r);
      return (
        <span className="bl-pill"
          title={r.ipAddress ?? ''}
          style={{ background: loc.bg, color: loc.fg, display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
          <i className={`ti ${loc.icon}`} style={{ fontSize: 12 }} /> {loc.label}
        </span>
      );
    },
  },
  { key: 'ipAddress',  header: 'IP Address',  width: '150px',
    render: v => v ? <code style={{ fontSize: 11, color: 'var(--ai-ink)', whiteSpace: 'nowrap', fontFamily: 'var(--ai-font-mono)' }}>{String(v)}</code> : <span style={{ color: 'var(--ai-muted)' }}>—</span> },
  { key: 'userAgent',  header: 'Device',      width: '180px',
    render: (_v, r) => (
      <span title={r.userAgent ?? ''} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--ai-ink)', whiteSpace: 'nowrap' }}>
        <i className={`ti ${osIcon(r.userAgent)}`} style={{ fontSize: 14, color: 'var(--ai-muted)', flexShrink: 0 }} />
        {uaShort(r.userAgent)}
      </span>
    ),
  },
];

// PAGE_SIZES + DEFAULT_PAGE_SIZE + pageSizeLabel sourced from shared listDefaults.ts.

/** Returns the same formatted string the user sees in the table cell — used
 *  by the multi-select column filter + search + sort. */
function getCellString(row: UserActivityRow, key: string): string {
  if (key === 'eventTime')  return fmtLocal(row.eventTime);
  if (key === 'userAgent')  return uaShort(row.userAgent);
  if (key === 'eventType')  return activityMeta(row.eventType).verb;
  if (key === 'location')   return locationLabel(row).label;
  if (key === 'detail')     return navText(row);
  if (key === 'durationMs') return fmtDuration(row.durationMs);
  const raw = row[key as keyof UserActivityRow];
  return raw === null || raw === undefined ? '' : String(raw);
}

// ─────────────────────────────────────────────────────────────────────────────
// "Active Users" — the live view over AI_UserSession.
// ─────────────────────────────────────────────────────────────────────────────
const activeCols = (onRelease: (r: ActiveSessionRow) => void): AppColumn<ActiveSessionRow>[] => [
  { field: 'userName', header: 'User', width: '190px',
    text: r => r.userName,
    render: r => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, color: 'var(--ai-ink)' }}>
        {/* A live dot, not a status pill: only a `type:'status'` column may be
            coloured, and "signed in" is the only state a row here can have. */}
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--ai-green)', flexShrink: 0 }} />
        {r.userName}
      </span>
    ),
  },
  { field: 'signedInAt', header: 'Signed in at', width: '165px',
    text: r => fmtLocal(r.signedInAt),
    render: r => <span style={{ color: 'var(--ai-ink)', whiteSpace: 'nowrap' }}>{fmtLocal(r.signedInAt)}</span>,
  },
  { field: 'sessionSeconds', header: 'Signed in for', width: '110px',
    text: r => fmtSeconds(r.sessionSeconds),
    render: r => <span style={{ color: 'var(--ai-ink)', whiteSpace: 'nowrap' }}>{fmtSeconds(r.sessionSeconds)}</span>,
  },
  { field: 'idleSeconds', header: 'Last seen', width: '110px',
    text: r => fmtSeconds(r.idleSeconds),
    // How long since their browser last checked in — the number that decides
    // when the idle sweep will release their licence.
    render: r => (
      <span style={{ color: 'var(--ai-ink)', whiteSpace: 'nowrap' }}>
        {r.idleSeconds < 90 ? 'just now' : `${fmtSeconds(r.idleSeconds)} ago`}
      </span>
    ),
  },
  { field: 'isExempt', header: 'Licence', width: '120px',
    text: r => (r.isExempt ? 'Not counted' : 'Using one'),
    render: r => (
      <span className={`bl-pill ${r.isExempt ? 'info' : 'ok'}`} style={{ whiteSpace: 'nowrap' }}>
        {r.isExempt ? 'Not counted' : 'Using one'}
      </span>
    ),
  },
  { field: 'ipAddress', header: 'IP Address', width: '140px',
    text: r => r.ipAddress ?? '',
    render: r => r.ipAddress
      ? <code style={{ fontSize: 11, color: 'var(--ai-ink)', whiteSpace: 'nowrap', fontFamily: 'var(--ai-font-mono)' }}>{r.ipAddress}</code>
      : <span style={{ color: 'var(--ai-muted)' }}>—</span>,
  },
  { field: 'userAgent', header: 'Device', width: '180px',
    text: r => uaShort(r.userAgent),
    render: r => (
      <span title={r.userAgent ?? ''} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--ai-ink)', whiteSpace: 'nowrap' }}>
        <i className={`ti ${osIcon(r.userAgent)}`} style={{ fontSize: 14, color: 'var(--ai-muted)', flexShrink: 0 }} />
        {uaShort(r.userAgent)}
      </span>
    ),
  },
  // Free a stuck licence without waiting out the idle timeout — for a machine
  // that died or someone who went home with the app open. Hidden for the exempt
  // support account, which never holds a licence, so the button cannot imply an
  // effect it would not have.
  // An action column, not data: no sort arrow, no filter, and excluded from
  // Export — a column of buttons exports as a column of blanks.
  // An action column, not data: no sort arrow, no filter, and excluded from
  // Export — a column of buttons exports as a column of blanks.
  { field: 'release', header: '', width: '130px',
    sortable: false, filterable: false, noExport: true,
    text: () => '',
    render: r => {
      // Exempt (service) sessions hold no licence, so there is nothing to free.
      if (r.isExempt) return <span style={{ color: 'var(--ai-muted)', fontSize: 11 }}>—</span>;

      // Release exists for a session left behind by a power cut or a browser
      // that closed without signing out.  While the person is still working,
      // the button is shown DISABLED rather than hidden: an admin looking for
      // it needs to see that it exists and why it is unavailable, otherwise
      // they conclude the feature is broken and go looking for another way.
      if (!r.canRelease) {
        return (
          <span
            title={r.isResponding
              ? `${r.userName} is using the application right now — releasing would sign them out mid-work.`
              : `${r.userName} has another session that is still active, so releasing this one would not free the licence.`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                     color: 'var(--ai-muted)', fontSize: 11, whiteSpace: 'nowrap' }}>
            <i className="ti ti-activity" style={{ fontSize: 13 }} />
            In use
          </span>
        );
      }

      return (
        <button className="ca-btn"
          title={`${r.userName}'s browser has stopped responding — free their licence now`}
          onClick={(e) => { e.stopPropagation(); onRelease(r); }}>
          <i className="ti ti-lock-open" style={{ marginRight: 4 }} />Release
        </button>
      );
    },
  },
];

/** One session: when it started, when it ended, and how. */
type SessionRow = {
  sessionId:      string | null;
  userName:       string;
  startedAt:      string;
  endedAt:        string | null;
  durationMs:     number | null;
  endReason:      string | null;
  isOpen:         boolean;
  /** Ended by idle timeout or token expiry — nobody signed out. */
  neverSignedOut: boolean;
  ipAddress:      string | null;
  userAgent:      string | null;
  location:       string | null;
};

type SessionMeta = {
  totalCount: number; returned: number;
  openCount: number; abandonedCount: number; unrecordedCount: number;
};

type DailyPeak = { day: string; peakUsers: number; sessions: number; users: number };
type FailedUser = { userName: string; attempts: number; distinctIps: number;
                    firstAttempt: string; lastAttempt: string };
type FailedIp   = { ipAddress: string; attempts: number; distinctAccounts: number;
                    lastAttempt: string };
type Insights   = { dailyPeak: DailyPeak[]; failedByUser: FailedUser[]; failedByIp: FailedIp[] };
type InsightsMeta = { days: number; licenceCap: number | null; observedPeak: number };

/** Plain English for how a session finished.  The distinction that matters is
 *  "the person ended it" vs "it was ended for them" — the second is the one a
 *  regulated lab has to care about. */
export function endReasonText(r: SessionRow): string {
  if (r.isOpen) return 'Still signed in';
  switch (r.endReason) {
    case 'logout':     return 'Clicked Sign Out';
    case 'tabclose':   return 'Closed the browser';
    case 'idle':       return 'Left signed in — timed out';
    case 'expired':    return 'Left signed in — session expired';
    case 'admin':      return 'Released by an administrator';
    case 'evicted':    return 'Signed out by the system';
    // Not a category of ending — an admission that the ending was never
    // written.  Saying so is better than implying the person is still on.
    case 'unrecorded': return 'No sign-out recorded';
    default:           return r.endReason ?? '—';
  }
}

const SESSION_COLS: AppColumn<SessionRow>[] = [
  { field: 'userName', header: 'User', width: '210px',
    render: r => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--ai-ink)' }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                       background: r.isOpen ? 'var(--ai-green)' : 'var(--ai-muted)' }} />
        {r.userName}
      </span>
    ),
  },
  { field: 'startedAt', header: 'Signed in', width: '155px',
    text: r => fmtLocalDateTime(r.startedAt) },
  { field: 'endedAt', header: 'Signed out', width: '175px',
    // The column this whole view exists for.  A blank would be ambiguous
    // between "still on" and "we never recorded it", so neither is ever blank.
    text: r => r.endedAt ? fmtLocalDateTime(r.endedAt) : (r.isOpen ? 'still signed in' : 'not recorded'),
    render: r => r.endedAt
      ? <span style={{ color: 'var(--ai-ink)' }}>{fmtLocalDateTime(r.endedAt)}</span>
      : <span style={{ color: 'var(--ai-muted)', fontStyle: 'italic' }}>
          {r.isOpen ? 'still signed in' : 'not recorded'}
        </span>,
  },
  { field: 'durationMs', header: 'Duration', width: '100px',
    text: r => fmtDuration(r.durationMs ?? undefined) },
  { field: 'endReason', header: 'How it ended', width: '215px',
    text: r => endReasonText(r),
    render: r => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
                     color: r.neverSignedOut ? 'var(--ai-amber, #b45309)' : 'var(--ai-ink)' }}>
        {r.neverSignedOut && <i className="ti ti-alert-triangle" style={{ fontSize: 13 }} />}
        {endReasonText(r)}
      </span>
    ),
  },
  { field: 'location',  header: 'Location',   width: '150px', text: r => r.location ?? '—' },
  { field: 'ipAddress', header: 'IP Address', width: '130px', text: r => r.ipAddress ?? '—' },
];

/** Build the login "session" that contains a clicked row: every event for the
 *  same user from the Login that started the session, up to (and including) the
 *  next Logout — or until the next Login (exclusive), or end of data. Runs on
 *  the loaded rows; sessions are short so the loaded window is enough. Returns
 *  the rows in chronological order. */
function buildSession(all: UserActivityRow[], clicked: UserActivityRow): UserActivityRow[] {
  // Exact pairing when the rows carry a session id — no guessing from adjacency,
  // which mis-grouped whenever the opening Login fell outside the loaded window.
  if (clicked.sessionId) {
    const exact = all
      .filter(r => r.sessionId === clicked.sessionId)
      .sort((a, b) => (parseUtc(a.eventTime)?.getTime() ?? 0) - (parseUtc(b.eventTime)?.getTime() ?? 0));
    if (exact.length > 0) return exact;
  }
  // Fallback for historic rows (and Navigation events, which carry no session):
  // walk this user's rows from the preceding Login to the next ending.
  const uname = (clicked.userName || '').toLowerCase();
  const mine = all
    .filter(r => (r.userName || '').toLowerCase() === uname)
    .slice()
    .sort((a, b) => (parseUtc(a.eventTime)?.getTime() ?? 0) - (parseUtc(b.eventTime)?.getTime() ?? 0));
  const idx = mine.findIndex(r => r.id === clicked.id);
  if (idx < 0) return [clicked];
  let start = 0;
  for (let i = idx; i >= 0; i--) { if (mine[i]!.eventType === 'Login') { start = i; break; } }
  let end = mine.length - 1;
  for (let i = start + 1; i < mine.length; i++) {
    if (mine[i]!.eventType === 'Logout') { end = i; break; }
    if (mine[i]!.eventType === 'Login')  { end = i - 1; break; }
  }
  return mine.slice(start, end + 1);
}

// ─────────────────────────────────────────────────────────────────────────────
// Export — the richer log column-set (a couple more than the on-screen grid:
// full User-Agent + DurationMs). Fed to the shared full-format <ExportMenu> via
// AppToolbar.exportData so all 10 ALPACA formats + ALL filtered rows export.
// ─────────────────────────────────────────────────────────────────────────────
const EXPORT_COLS: { header: string; value: (r: UserActivityRow) => string }[] = [
  { header: 'Date / Time (local)', value: r => fmtLocal(r.eventTime) },
  { header: 'User',                value: r => r.userName },
  { header: 'Action',              value: r => activityMeta(r.eventType).verb },
  { header: 'Reason / Page',       value: r => navText(r) },
  { header: 'Location',            value: r => locationLabel(r).label },
  { header: 'IP',                  value: r => r.ipAddress ?? '' },
  { header: 'Device',              value: r => uaShort(r.userAgent) },
  { header: 'User-Agent (full)',   value: r => r.userAgent ?? '' },
  { header: 'Duration',            value: r => fmtDuration(r.durationMs) },
  { header: 'DurationMs',          value: r => r.durationMs == null ? '' : String(r.durationMs) },
  { header: 'Session Id',          value: r => r.sessionId ?? '' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Settings → User Access Log.  Service / admin only — gated client-side via
 * SERVICE_ONLY in navPermissions.ts AND server-side in AdminActivityController.
 *
 * Data source: <c>AI_UserActivityLog</c>.  All times stored UTC; rendered in
 * the viewer's local time zone via <c>Intl.DateTimeFormat</c>.
 */
export default function UserActivityPage() {
  const [view,        setView]        = useState<LogView>('history');
  const [sessions,    setSessions]    = useState<SessionRow[]>([]);
  const [sessMeta,    setSessMeta]    = useState<SessionMeta | null>(null);
  const [endedBy,     setEndedBy]     = useState<'all' | 'abandoned' | 'open' | 'deliberate'>('all');
  const [insights,    setInsights]    = useState<Insights | null>(null);
  const [insMeta,     setInsMeta]     = useState<InsightsMeta | null>(null);
  const [rows,        setRows]        = useState<UserActivityRow[]>([]);
  const [totalCount,  setTotalCount]  = useState(0);
  const [limit,       setLimit]       = useState(PAGE_STEP);
  const [loading,     setLoading]     = useState(false);
  const [loadMs,      setLoadMs]      = useState<number | null>(null);
  const [error,       setError]       = useState<string>();
  const [eventFilter, setEventFilter] = useState<EventFilter>('all');
  const [dateRange,   setDateRange]   = useState<DateRangeKey>('7d');
  const [userInput,   setUserInput]   = useState('');
  const [search,      setSearch]      = useState('');
  const [session,     setSession]     = useState<{ user: string; rows: UserActivityRow[] } | null>(null);
  const [active,      setActive]      = useState<ActiveSessionRow[]>([]);
  const [activeMeta,  setActiveMeta]  = useState<ActiveMeta | null>(null);
  const abortRef  = useRef<AbortController>();
  const confirm = useConfirm();
  const notify = useNotify();

  /** Free a stuck licence immediately. Confirmed first — this signs a real person
   *  out of every browser they have open, so it must never be a stray click. */
  const releaseLicence = async (r: ActiveSessionRow) => {
    const idleMin = Math.round(r.idleSeconds / 60);
    const ok = await confirm.confirm({
      title: 'Release this licence?',
      // Lead with the EVIDENCE that the session is dead. The admin's real
      // question is "am I about to interrupt someone?", so answer it before
      // asking them to confirm.
      message: `${r.userName}'s browser has not responded for `
             + `${idleMin >= 1 ? `${idleMin} minute${idleMin === 1 ? '' : 's'}` : `${r.idleSeconds} seconds`}, `
             + 'so it is treated as gone — a power cut, a crash, or a window closed without '
             + 'signing out. Releasing frees their concurrent-user licence immediately. '
             + 'If they are in fact still at their desk, they will simply be asked to sign in again.',
      tone: 'danger',
      confirmLabel: 'Release',
    });
    if (!ok) return;
    try {
      const res = await api.post<{ success: boolean; data: { released: number; message: string } }>(
        `/api/v1/admin/activity/active/${r.userOid}/release`, {});
      if (res.data.success) {
        notify.success(res.data.data.message);
        await loadActive(true);
      }
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { error?: { code?: string; message?: string } } } };
      const status = err?.response?.status;
      // 409 = they came back to life between the page loading and the click.
      // Show the server's own explanation rather than a generic failure, and
      // refresh so the row stops offering an action that is no longer valid.
      if (status === 409) {
        notify.error(err.response?.data?.error?.message
          ?? 'That user is active again — their licence was not released.');
        await loadActive(true);
        return;
      }
      notify.error(status === 403
        ? 'Service / Administrator access is required to release a licence.'
        : 'Could not release the licence. Please try again.');
    }
  };

  const openSession = (r: UserActivityRow) =>
    setSession({ user: r.userName, rows: buildSession(rows, r) });

  const loadData = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true); setError(undefined);
    const t0 = performance.now();
    try {
      const params: Record<string, string> = { top: String(limit) };
      const types = EVENT_FILTERS[eventFilter];
      if (types)                             params.eventType = types;
      if (userInput.trim())                  params.user      = userInput.trim();
      const since = cutoffSinceUtc(dateRange);
      if (since)                             params.sinceUtc  = since;
      const qs = new URLSearchParams(params).toString();

      const res = await api.get<{
        success: boolean;
        data: UserActivityRow[];
        meta?: { totalCount?: number };
      }>(`/api/v1/admin/activity?${qs}`, { signal: ctrl.signal });
      if (!ctrl.signal.aborted) {
        if (res.data.success) {
          setRows(res.data.data);
          // The server's TRUE match count, not the page size — so the KPIs stop
          // silently meaning "…of the first 1000".
          setTotalCount(res.data.meta?.totalCount ?? res.data.data.length);
        } else setError('Failed to load activity log.');
      }
    } catch (e: unknown) {
      if (!ctrl.signal.aborted) {
        const msg = e instanceof Error ? e.message : 'Unknown error';
        if (msg !== 'canceled' && msg !== 'CanceledError') setError(msg);
      }
    } finally {
      if (!ctrl.signal.aborted) {
        setLoadMs(performance.now() - t0);
        setLoading(false);
      }
    }
  }, [eventFilter, userInput, dateRange, limit]);

  /** The live view. Marked `silentLoading` so the 15-second auto-refresh does
   *  not flash the global progress bar every quarter-minute. */
  const loadActive = useCallback(async (showSpinner: boolean) => {
    if (showSpinner) { setLoading(true); setError(undefined); }
    const t0 = performance.now();
    try {
      const res = await api.get<{ success: boolean; data: ActiveSessionRow[]; meta: ActiveMeta }>(
        // `as never` is the established way this codebase passes the custom
        // silentLoading flag through Axios's config type (see PrepBatchDetailPage).
        '/api/v1/admin/activity/active', { silentLoading: !showSpinner } as never);
      if (res.data.success) { setActive(res.data.data); setActiveMeta(res.data.meta); }
    } catch (e: unknown) {
      if (showSpinner) setError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      if (showSpinner) { setLoadMs(performance.now() - t0); setLoading(false); }
    }
  }, []);

  // Filters only apply to the history view; don't re-query it while the user is
  // watching the live tab.
  useEffect(() => { if (view === 'history') loadData(); }, [loadData, view]);

  const loadSessions = useCallback(async () => {
    const t0 = performance.now();
    setLoading(true); setError(undefined);
    try {
      const res = await api.get<{ success: boolean; data: SessionRow[]; meta: SessionMeta }>(
        '/api/v1/admin/activity/sessions',
        { params: { endedBy, top: 500 } });
      setSessions(res.data.data ?? []);
      setSessMeta(res.data.meta ?? null);
    } catch {
      setError('Could not load sessions.');
    } finally {
      setLoadMs(Math.round(performance.now() - t0));
      setLoading(false);
    }
  }, [endedBy]);

  const loadInsights = useCallback(async () => {
    const t0 = performance.now();
    setLoading(true); setError(undefined);
    try {
      const res = await api.get<{ success: boolean; data: Insights; meta: InsightsMeta }>(
        '/api/v1/admin/activity/insights', { params: { days: 30 } });
      setInsights(res.data.data ?? null);
      setInsMeta(res.data.meta ?? null);
    } catch {
      setError('Could not load insights.');
    } finally {
      setLoadMs(Math.round(performance.now() - t0));
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (view === 'sessions') loadSessions(); }, [loadSessions, view]);
  useEffect(() => { if (view === 'insights') loadInsights(); }, [loadInsights, view]);

  // "Who is on now" is only true for as long as it takes to read it, so it
  // refreshes itself. The interval is cleared on unmount and when the user
  // leaves the tab — a background poll against a service-only endpoint would be
  // pure waste.
  useEffect(() => {
    if (view !== 'active') return;
    loadActive(true);
    const t = setInterval(() => loadActive(false), 15_000);
    return () => clearInterval(t);
  }, [view, loadActive]);

  // Global search box + event-type tabs + server-side user filter feed the grid;
  // the shared <AppDataGrid> owns per-column filtering, sorting and pagination.
  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    if (!s) return rows;
    return rows.filter(row =>
      // Detail isn't a visible column, but keep it searchable so "Search any
      // column" still finds a page/detail by name.
      getCellString(row, 'detail').toLowerCase().includes(s) ||
      COLS.some(c => getCellString(row, c.key as string).toLowerCase().includes(s)),
    );
  }, [rows, search]);

  // Shared full-format "Export to" — ALL filtered rows, all 10 ALPACA formats.
  const exportData: ExportData = {
    filename: 'user-access-log',
    headers: EXPORT_COLS.map(c => c.header),
    rows: filtered.map(r => EXPORT_COLS.map(c => c.value(r))),
  };

  /** The live view exports too — an auditor asking "who was on at 14:00" wants
   *  the snapshot, not just the history. */
  const activeExportData: ExportData = {
    filename: 'active-users',
    headers: ['User', 'Signed in at', 'Signed in for', 'Last seen', 'Licence', 'IP', 'Device'],
    rows: active.map(s => [
      s.userName,
      fmtLocal(s.signedInAt),
      fmtSeconds(s.sessionSeconds),
      `${fmtSeconds(s.idleSeconds)} ago`,
      s.isExempt ? 'Not counted' : 'Using one',
      s.ipAddress ?? '',
      uaShort(s.userAgent),
    ]),
  };

  // Map the existing column defs onto AppDataGrid columns — custom cell
  // renderers (pills, icons) are preserved; filtering/sorting use the same
  // displayed text (getCellString) so behaviour matches the old grid.
  const gridCols = useMemo<AppColumn<UserActivityRow>[]>(
    () => COLS.map(c => ({
      field:  c.key as string,
      header: c.header,
      width:  c.width,
      render: c.render ? (r: UserActivityRow) => c.render!(r[c.key as keyof UserActivityRow], r) : undefined,
      text:   (r: UserActivityRow) => getCellString(r, c.key as string),
    })),
    [],
  );

  // KPIs — counted over the LOADED rows, and labelled as such when the range
  // holds more than we fetched (see `truncated`), rather than presenting a
  // partial count as a total.
  const logins    = rows.filter(r => r.eventType === 'Login').length;
  const failed    = rows.filter(r => r.eventType === 'LoginFailed').length;
  const signOuts  = rows.filter(r => r.eventType === 'Logout' || r.eventType === 'SessionTimeout').length;
  const timeouts  = rows.filter(r => r.eventType === 'SessionTimeout').length;
  const uniqUsers = useMemo(
    () => new Set(rows.map(r => r.userName.toLowerCase())).size,
    [rows],
  );
  const truncated = totalCount > rows.length;
  const canLoadMore = truncated && limit < MAX_ROWS;

  return (
    <div className="bl-page">

      {/* ── Fixed top ── */}
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Settings</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">User Access Log</span>
          </div>
          {/* Shared FormTitle — the ONE page heading (§18.6) ON the grey page. */}
          <div className="dp-title-row">
            <FormTitle pageId="useractivity" loadMs={loadMs} loading={loading} />
          </div>
          <div className="dp-subtitle">
            <i className="ti ti-clock-hour-4" style={{ fontSize: 13, verticalAlign: 'middle', marginRight: 4 }} />
            Local time (<strong>{LOCAL_TZ}</strong>), 24-hour ·
            Service-only audit ·
            {view === 'active'
              ? (loading ? ' …' : ` ${active.length} live session${active.length === 1 ? '' : 's'}`)
              : (loading ? ' …' : ` ${totalCount.toLocaleString()} events`)}
          </div>
        </div>

        {/* View switch — the two questions this page answers. Kept as tabs (not
            a separate nav entry) so the access log stays ONE form, per the
            "extend the shared form, never fork it" rule. */}
        <div className="sa-filter-row" style={{ marginBottom: 8 }}>
          <span className="sa-filter-lbl">View:</span>
          <button className={`sa-filter-btn${view === 'history' ? ' active' : ''}`}
            onClick={() => setView('history')}>
            <i className="ti ti-history" style={{ fontSize: 13, marginRight: 4 }} />History
          </button>
          <button className={`sa-filter-btn${view === 'active' ? ' active' : ''}`}
            onClick={() => setView('active')}>
            <i className="ti ti-users" style={{ fontSize: 13, marginRight: 4 }} />
            Active Users{activeMeta?.tracking ? ` (${activeMeta.activeSessions})` : ''}
          </button>
          <button className={`sa-filter-btn${view === 'sessions' ? ' active' : ''}`}
            onClick={() => setView('sessions')}>
            <i className="ti ti-login" style={{ fontSize: 13, marginRight: 4 }} />Sessions
          </button>
          <button className={`sa-filter-btn${view === 'insights' ? ' active' : ''}`}
            onClick={() => setView('insights')}>
            <i className="ti ti-chart-bar" style={{ fontSize: 13, marginRight: 4 }} />Insights
          </button>
        </div>

        {error && (
          <div className="bl-err">
            <i className="ti ti-alert-circle" style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 5 }} />
            {error}
          </div>
        )}

        {/* KPI row — swaps with the view, because "logins in the last 7 days"
            and "signed in right now" are different questions. */}
        {view === 'sessions' ? (
          <div className="kpi-row">
            <div className="kpi">
              <div className="kpi-lbl">Sessions</div>
              <div className="kpi-val kpi-blue">{loading ? '…' : (sessMeta?.totalCount ?? 0).toLocaleString()}</div>
              <div className="kpi-chg">Sign-in to sign-out</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Left signed in</div>
              {/* The compliance number: nobody signed out, the timer did it. */}
              <div className="kpi-val" style={{ color: 'var(--ai-amber, #b45309)' }}>
                {loading ? '…' : (sessMeta?.abandonedCount ?? 0).toLocaleString()}
              </div>
              <div className="kpi-chg">Timed out, never signed out</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Still signed in</div>
              <div className="kpi-val kpi-teal">{loading ? '…' : (sessMeta?.openCount ?? 0).toLocaleString()}</div>
              <div className="kpi-chg">Live right now</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">No ending recorded</div>
              <div className="kpi-val" style={{ color: 'var(--ai-muted)' }}>
                {loading ? '…' : (sessMeta?.unrecordedCount ?? 0).toLocaleString()}
              </div>
              {/* Kept separate from "left signed in": one is a person's habit,
                  the other is our own past failure to write the row. */}
              <div className="kpi-chg">Mostly before 25 Jul 2026</div>
            </div>
          </div>
        ) : view === 'insights' ? (
          <div className="kpi-row">
            <div className="kpi">
              <div className="kpi-lbl">Licence</div>
              <div className="kpi-val kpi-blue">{insMeta?.licenceCap ?? '∞'}</div>
              <div className="kpi-chg">Concurrent users allowed</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Highest reached</div>
              <div className="kpi-val" style={{
                color: insMeta && insMeta.licenceCap != null && insMeta.observedPeak > insMeta.licenceCap
                  ? 'var(--ai-amber, #b45309)' : 'var(--ai-green)' }}>
                {loading ? '…' : (insMeta?.observedPeak ?? 0)}
              </div>
              <div className="kpi-chg">In the last {insMeta?.days ?? 30} days</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Failed sign-ins</div>
              <div className="kpi-val" style={{ color: (insights?.failedByUser.length ?? 0) > 0
                  ? 'var(--ai-amber, #b45309)' : 'var(--ai-green)' }}>
                {loading ? '…' : (insights?.failedByUser.reduce((a, f) => a + f.attempts, 0) ?? 0)}
              </div>
              <div className="kpi-chg">Accounts with repeated failures</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Busiest day</div>
              <div className="kpi-val kpi-teal">
                {loading ? '…' : (insights?.dailyPeak.reduce((a, d) => Math.max(a, d.sessions), 0) ?? 0)}
              </div>
              <div className="kpi-chg">Sessions in one day</div>
            </div>
          </div>
        ) : view === 'active' ? (
          <div className="kpi-row">
            <div className="kpi">
              <div className="kpi-lbl">Signed in now</div>
              <div className="kpi-val kpi-teal">{loading ? '…' : (activeMeta?.activeUsers ?? 0)}</div>
              <div className="kpi-chg">Distinct users using a licence</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Live sessions</div>
              <div className="kpi-val kpi-blue">{loading ? '…' : (activeMeta?.activeSessions ?? 0)}</div>
              {/* One person on a PC and a laptop is 2 sessions but 1 licence —
                  spelling that out here prevents the usual "why is the count
                  higher than the licence?" question. */}
              <div className="kpi-chg">One user may hold several</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Inactivity timeout</div>
              <div className="kpi-val" style={{ color: 'var(--ai-green)' }}>{activeMeta?.idleTimeoutMinutes ?? '—'}<span style={{ fontSize: 14 }}> min</span></div>
              {/* Screen Auto Lock — the one app-wide window (2026-07-30), not a
                  licensing-only setting. Named so nobody hunts for a second one. */}
              <div className="kpi-chg">Screen Auto Lock</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Auto-refresh</div>
              <div className="kpi-val" style={{ color: '#4f46e5' /* token-exempt: chart */ }}>15<span style={{ fontSize: 14 }}> sec</span></div>
              <div className="kpi-chg">This view updates itself</div>
            </div>
          </div>
        ) : (
          <div className="kpi-row">
            <div className="kpi">
              <div className="kpi-lbl">Total Events</div>
              <div className="kpi-val kpi-blue">{loading ? '…' : totalCount.toLocaleString()}</div>
              <div className="kpi-chg">{loading ? '…' : `${filtered.length.toLocaleString()} loaded`}</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Unique Users</div>
              <div className="kpi-val kpi-teal">{loading ? '…' : uniqUsers}</div>
              <div className="kpi-chg">Distinct accounts</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Sign-ins</div>
              <div className="kpi-val" style={{ color: 'var(--ai-green)' }}>{loading ? '…' : logins.toLocaleString()}</div>
              <div className="kpi-chg" style={{ color: failed > 0 ? 'var(--ai-red)' : undefined }}>
                {failed > 0 ? `${failed.toLocaleString()} refused` : 'none refused'}
              </div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Sign-outs</div>
              <div className="kpi-val" style={{ color: '#4f46e5' /* token-exempt: chart */ }}>{loading ? '…' : signOuts.toLocaleString()}</div>
              <div className="kpi-chg">{timeouts.toLocaleString()} timed out</div>
            </div>
          </div>
        )}
      </div>

      {/* ── Grid area: toolbar + search sit ON the grey page (BO UI Standard v2);
          only the grid card below is white. ── */}
      <div className="bl-grid-area">
        {/* Toolbar on its OWN full-width row — read-only audit (no CRUD):
            date-range (left) · Refresh · Export to.  The Export-to menu keeps
            its dropdown so it stays anchored, rendered through the right slot. */}
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={view === 'history'
              ? (
                <DateRangeSelect variant="dropdown" label="Date Range"
                  ranges={[{ label: '24h', days: 1 }, { label: '7d', days: 7 }, { label: '30d', days: 30 }, { label: '90d', days: 90 }, { label: 'All', days: null }]}
                  value={({ '24h': 1, '7d': 7, '30d': 30, '90d': 90, all: null } as Record<DateRangeKey, number | null>)[dateRange]}
                  onChange={(d) => setDateRange(d === 1 ? '24h' : d === 7 ? '7d' : d === 30 ? '30d' : d === 90 ? '90d' : 'all')} />
              )
              // A date range is meaningless for "right now".
              : undefined}
            onRefresh={view === 'history' ? loadData : () => loadActive(true)}
            refreshing={loading}
            exportData={view === 'history' ? exportData : activeExportData}
          />
        </div>

        {/* Search row + user filter — shared .list-search-row on the grey page,
            never inside the card. */}
        {view === 'history' && (
          <div className="list-search-row">
            <div className="list-search">
              <span className="tb-i">🔍</span>
              <input
                type="text"
                placeholder="Search any column…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
            </div>
            <div className="list-search" style={{ maxWidth: 260 }}>
              <i className="ti ti-user" />
              <input
                type="text"
                // Partial match now works server-side; the old exact-only match
                // returned nothing for a half-typed name and said why.
                placeholder="Filter by user (any part of the name)…"
                value={userInput}
                onChange={e => setUserInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') loadData(); }}
              />
              {userInput && (
                <span className="list-search-clear"
                  onClick={() => { setUserInput(''); }}>×</span>
              )}
            </div>
            {filtered.length !== rows.length && (
              <span className="search-match-count">
                {filtered.length} match{filtered.length !== 1 ? 'es' : ''}
              </span>
            )}
            {/* Never silently truncate: say what is missing and offer to fetch it. */}
            {truncated && !loading && (
              <span className="search-match-count" style={{ color: 'var(--ai-red)' }}>
                Showing {rows.length.toLocaleString()} of {totalCount.toLocaleString()}
                {canLoadMore ? (
                  <button className="ca-btn" style={{ marginLeft: 8 }}
                    onClick={() => setLimit(l => Math.min(l + PAGE_STEP, MAX_ROWS))}>
                    Load more
                  </button>
                ) : ' — narrow the date range or filter by user to see the rest'}
              </span>
            )}
          </div>
        )}

        {/* ONE white grid card — filter tabs + grid + its single footer. */}
        <div className="bl-card bl-card-flex">
        {view === 'sessions' ? (
          <>
            <div className="sa-filter-row" style={{ marginBottom: 8 }}>
              <span className="sa-filter-lbl">Show:</span>
              {([
                { id: 'all',        label: 'All sessions' },
                { id: 'abandoned',  label: 'Left signed in' },
                { id: 'deliberate', label: 'Signed out properly' },
                { id: 'open',       label: 'Still signed in' },
              ] as { id: typeof endedBy; label: string }[]).map(t => (
                <button key={t.id}
                  className={`sa-filter-btn${endedBy === t.id ? ' active' : ''}`}
                  onClick={() => setEndedBy(t.id)}>
                  {t.label}
                </button>
              ))}
            </div>
            <AppDataGrid<SessionRow>
              rows={sessions}
              // SessionId is null for a login whose ending was never recorded, so
              // it cannot be the row key; the start time + user is unique enough.
              getRowId={r => r.sessionId ?? `${r.userName}|${r.startedAt}`}
              columns={SESSION_COLS}
              loading={loading}
              loadMs={loadMs}
              defaultPageSize={20}
              emptyText="No sessions match the current filter."
            />
          </>
        ) : view === 'insights' ? (
          <div style={{ padding: '4px 12px 14px' }}>
            {/* ── capacity ───────────────────────────────────────────────── */}
            <div className="sec-gridcard" style={{ padding: 14, marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ai-ink)', marginBottom: 2 }}>
                Peak concurrent users per day
              </div>
              <div style={{ fontSize: 11, color: 'var(--ai-muted)', marginBottom: 12 }}>
                The most people signed in at the same moment each day, counted the way the
                licence counts them — one person on three machines is one user. The service
                account is excluded because it uses no licence.
              </div>
              {insMeta && insMeta.licenceCap != null && insMeta.observedPeak > insMeta.licenceCap && (
                <div className="bl-err" style={{ marginBottom: 12 }}>
                  <i className="ti ti-alert-triangle" style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 5 }} />
                  Demand has reached {insMeta.observedPeak} concurrent users, but the licence
                  allows {insMeta.licenceCap}. On those days someone was turned away.
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 150,
                            borderBottom: '1px solid var(--ai-border, #e5e7eb)', paddingBottom: 2 }}>
                {(insights?.dailyPeak ?? []).map(d => {
                  const max  = Math.max(1, ...(insights?.dailyPeak ?? []).map(x => x.peakUsers));
                  const over = insMeta?.licenceCap != null && d.peakUsers > insMeta.licenceCap;
                  return (
                    <div key={d.day} title={`${d.day} — peak ${d.peakUsers} concurrent user(s), ${d.sessions} session(s)`}
                         style={{ flex: 1, display: 'flex', flexDirection: 'column',
                                  alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                      <span style={{ fontSize: 10, color: 'var(--ai-muted)', marginBottom: 2 }}>{d.peakUsers}</span>
                      <div style={{ width: '100%', maxWidth: 26, borderRadius: '3px 3px 0 0',
                                    height: `${Math.max(3, (d.peakUsers / max) * 118)}px`,
                                    background: over ? 'var(--ai-amber, #b45309)' : 'var(--ai-loader-color, #e8670a)' }} />
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                {(insights?.dailyPeak ?? []).map((d, i, arr) => (
                  <div key={d.day} style={{ flex: 1, textAlign: 'center', fontSize: 9,
                                            color: 'var(--ai-muted)', overflow: 'hidden' }}>
                    {/* Only every other label on a crowded axis, so they stay legible. */}
                    {arr.length <= 16 || i % 2 === 0 ? d.day.slice(5) : ''}
                  </div>
                ))}
              </div>
              {(insights?.dailyPeak.length ?? 0) === 0 && !loading && (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--ai-muted)', fontSize: 12 }}>
                  No sessions recorded in this window yet.
                </div>
              )}
            </div>

            {/* ── failed sign-ins ────────────────────────────────────────── */}
            <div className="sec-gridcard" style={{ padding: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ai-ink)', marginBottom: 2 }}>
                Repeated failed sign-ins
              </div>
              <div style={{ fontSize: 11, color: 'var(--ai-muted)', marginBottom: 12 }}>
                Accounts with 3 or more failures in the last 24 hours. One account failing from
                one address is usually a forgotten password; one address failing against several
                accounts is not.
              </div>
              {(insights?.failedByUser.length ?? 0) === 0 ? (
                <div style={{ padding: '14px 0', color: 'var(--ai-green)', fontSize: 12 }}>
                  <i className="ti ti-circle-check" style={{ marginRight: 5, verticalAlign: 'middle' }} />
                  Nothing to look at — no account has failed repeatedly.
                </div>
              ) : (
                <table className="bl-tbl" style={{ width: '100%' }}>
                  <thead>
                    <tr><th>Account</th><th>Attempts</th><th>Addresses</th><th>Last attempt</th></tr>
                  </thead>
                  <tbody>
                    {insights!.failedByUser.map(f => (
                      <tr key={f.userName}>
                        <td>{f.userName}</td>
                        <td>{f.attempts}</td>
                        <td>{f.distinctIps}</td>
                        <td>{fmtLocalDateTime(f.lastAttempt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {insights?.failedByIp.some(f => f.distinctAccounts > 1) && (
                <div className="bl-err" style={{ marginTop: 12 }}>
                  <i className="ti ti-alert-triangle" style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 5 }} />
                  {insights.failedByIp.filter(f => f.distinctAccounts > 1).map(f =>
                    `${f.ipAddress} failed against ${f.distinctAccounts} different accounts`).join('; ')}.
                </div>
              )}
            </div>
          </div>
          ) : view === 'history' ? (
            <>
              {/* Event-type tabs. Counts are omitted deliberately: they could only
                  ever count the LOADED rows, and a number that quietly means
                  "…so far" next to a tab label reads as a total. */}
              <div className="sa-filter-row">
                <span className="sa-filter-lbl">Action:</span>
                {([
                  { id: 'all',     label: 'All' },
                  { id: 'signin',  label: 'Sign-ins' },
                  { id: 'signout', label: 'Sign-outs' },
                  { id: 'nav',     label: 'Page Views' },
                ] as { id: EventFilter; label: string }[]).map(t => (
                  <button
                    key={t.id}
                    className={`sa-filter-btn${eventFilter === t.id ? ' active' : ''}`}
                    onClick={() => { setEventFilter(t.id); setLimit(PAGE_STEP); }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Shared data grid — visual style, column filters, sort, footer /
                  pagination and the load-time indicator are all standardised in
                  <AppDataGrid> (CLAUDE.md §18.6).  This page passes only data,
                  columns and a row-click handler. */}
              <AppDataGrid<UserActivityRow>
                rows={filtered}
                columns={gridCols}
                getRowId={(r) => r.id}
                loading={loading}
                loadMs={loadMs}
                defaultPageSize={20}
                onRowClick={openSession}
                emptyText="No activity events match the current filters."
              />
            </>
          ) : (
            <>
              {/* Enforcement off ⇒ no session rows exist. Saying so is essential:
                  an empty grid would otherwise read as "nobody is signed in". */}
              {activeMeta && !activeMeta.tracking && (
                <div className="bl-err" style={{ margin: '10px 12px' }}>
                  <i className="ti ti-info-circle" style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 5 }} />
                  {activeMeta.message}
                </div>
              )}
              <AppDataGrid<ActiveSessionRow>
                rows={active}
                columns={activeCols(releaseLicence)}
                getRowId={(r) => r.sessionId}
                loading={loading}
                loadMs={loadMs}
                defaultPageSize={20}
                emptyText={activeMeta && !activeMeta.tracking
                  ? 'Live sessions are not being tracked.'
                  : 'Nobody is signed in right now.'}
              />
            </>
          )}
        </div>
      </div>

      {/* ── Session timeline detail (click a row) ── */}
      {session && (
        <div
          onClick={() => setSession(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000,
                   display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, width: 'min(720px, 96vw)', maxHeight: '88vh',
                     display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,.3)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '14px 18px', borderBottom: '1px solid var(--ai-line)', background: 'var(--ai-header-bg)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className="ti ti-timeline-event" style={{ fontSize: 18, color: '#4f46e5' /* token-exempt: chart */ }} />
                <div>
                  <div style={{ fontWeight: 700, color: 'var(--ai-ink)' }}>Session timeline — {session.user}</div>
                  <div style={{ fontSize: 12, color: 'var(--ai-muted)' }}>
                    {session.rows.length} action{session.rows.length !== 1 ? 's' : ''}
                    {session.rows.length > 0 && (
                      <> · {fmtLocal(session.rows[0]?.eventTime)} → {fmtLocal(session.rows[session.rows.length - 1]?.eventTime)}</>
                    )}
                  </div>
                </div>
              </div>
              <button className="ca-btn" onClick={() => setSession(null)}><i className="ti ti-x" /></button>
            </div>

            {session.rows.length > 0 && (() => {
              const f = session.rows[0]!;
              const l = session.rows[session.rows.length - 1]!;
              // Prefer the duration the SERVER recorded on the ending event —
              // one clock, and it covers the whole session even when the middle
              // events fall outside the loaded window. Fall back to first→last
              // only for historic rows that have no ending.
              const ended = session.rows.find(r => r.durationMs != null);
              const ms = ended?.durationMs
                ?? ((parseUtc(l.eventTime)?.getTime() ?? 0) - (parseUtc(f.eventTime)?.getTime() ?? 0));
              const durLabel = fmtDuration(ms) || '0s';
              const loc = locationLabel(f);
              return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, padding: '10px 18px', fontSize: 12,
                              color: 'var(--ai-muted)', borderBottom: '1px solid var(--ai-line-soft)' }}>
                  <span><i className="ti ti-clock" style={{ marginRight: 4 }} />Duration: <strong>{durLabel}</strong>{ended ? '' : ' (to last action)'}</span>
                  <span><i className={`ti ${loc.icon}`} style={{ marginRight: 4 }} />{loc.label}</span>
                  <span><i className="ti ti-network" style={{ marginRight: 4 }} />{f.ipAddress ?? '—'}</span>
                  <span><i className={`ti ${osIcon(f.userAgent)}`} style={{ marginRight: 4 }} />{uaShort(f.userAgent)}</span>
                </div>
              );
            })()}

            <div style={{ overflowY: 'auto', padding: '8px 18px 16px' }}>
              {session.rows.map((s, i) => {
                const a = activityMeta(s.eventType);
                return (
                  <div key={s.id} style={{ display: 'flex', gap: 10, padding: '8px 0', alignItems: 'flex-start',
                                           borderBottom: i < session.rows.length - 1 ? '1px dashed var(--ai-line-soft)' : 'none' }}>
                    <div style={{ width: 150, flexShrink: 0, fontSize: 12, color: 'var(--ai-muted)', whiteSpace: 'nowrap' }}>
                      {fmtLocal(s.eventTime)}
                    </div>
                    <span className={`bl-pill ${a.cls}`} style={{ flexShrink: 0 }}>
                      <i className={`ti ${a.icon}`} style={{ fontSize: 12 }} /> {a.verb}
                    </span>
                    <div style={{ fontSize: 13, color: 'var(--ai-ink)' }}>
                      {navText(s) || <span style={{ color: 'var(--ai-muted)' }}>—</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
